import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Control, useWatch } from 'react-hook-form';
import './style.scss';
import _ from 'lodash';
import UIUtils, { DefaultSwitch } from 'services/UIUtils';
import CPPUtils from 'services/CPPUtils';
import { isControlledSchedule } from 'services/PrescriptionUtils';
import PreviewDocumentModal from 'components/Modals/PreviewDocumentModal';
import PharmacySearchModal from 'components/Modals/PharmacySearchModal';
import DefaultModal from 'components/Utils/DefaultModal';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { TopBar } from 'components/Patient/CPP/CPPInput/Inputs/CPPInputCommonViews';
import PharmacySelector from './PharmacySelector';
import StackItemsView from 'components/Common/StackItemsView';
import DefaultReactSelect from 'components/Utils/DefaultReactSelect';
import DocumentTemplate from 'components/HealthRecords/Documents/DocumentTemplate';
import Icon from 'components/Utils/NewIcon';
import AddDocumentAttachmentModal from 'components/Modals/AddDocumentAttachmentModal';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useDefaultPharmacy, useValidPreviousPharmacies } from 'hooks/useDefaultPharmacy';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import MedicalOrderModal from 'components/Modals/MedicalOrder';
import { Calculator } from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote/AppointmentNoteSections';
import PediatricDoseCalculator from 'components/Modals/PediatricDoseCalculatorModal';
import CircularProgress from '@material-ui/core/CircularProgress';
import OdbCoverageInfo from '../OdbCoverageInfo';
import DosingInstructionsInput from './DosingInstructionsInput';
import ConfirmationButton from '../../../Utils/NewDefaultButton/ConfirmationButton';
import { useGeneratePrescription } from '../../../Patient/CPP/AISuggestionsBar';
import { useCPPContext } from '../../../Patient/CPP/CPPContext';
import { IconButton, Tooltip } from '@material-ui/core';
import Info from 'scenes/HomeStaff/StatisticsDashboard/components/info';
import AddToFavoritesModal from '../AddToFavoritesModal';
import FavoritesTagManager from '../FavoritesTagManager';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultAutoComplete from 'components/Utils/DefaultAutoComplete';
import TextareaAutosize from 'components/Common/TextAreaAutosize';

const NewPrescriptionView = (props: any) => {
  let {
    pharmacy,
    loadingPreview,
    loadingAdd,
    medications,
    matchingAllergies,
    loadingAllergies,
    previewPrescription,
    setPharmacy,
    onSelectPharmacy,
    addMedication,
    clearMedication,
    deleteMedication,
    editMedication,
    favorites,
    hideFavorites,
    removePharmacy,
    onCrossTaper,
    loadingCrossTaper,
    showCloseButton,
    showAISuggestions,
    // RHF props
    control,
    handleSubmit,
    setValue,
    isValid,
  } = props;

  const appointment = useAppSelector((state) => state.ui.activeBubble?.appointment);
  const [showMedicalOrderModal, setShowMedicalOrderModal] = useState(false);
  const drugbankProduct = useWatch({ control, name: 'drugbank_product' });
  // When the AI suggestions sidebar is showing, the drug-details card moves into
  // that right rail (below the suggestions) instead of sitting beside the form,
  // so the two don't both compete for the right side.
  const aiSuggestionsVisible = showAISuggestions && !!appointment;

  function clearMedications() {
    setValue('medications', []);
  }

  function getMedicationInteraction(medication: any) {
    const allergyIcons = loadingAllergies?.includes(medication.id) ? (
      <CircularProgress size={20} style={{ marginRight: '5px' }} />
    ) : (
      matchingAllergies[medication.id]?.map(({ allergy_name }: any) => (
        <Icon
          key={allergy_name}
          src="circle-exclamation"
          alt={`Allergic interaction with ${allergy_name}`}
          showTooltip={true}
          className="red"
          style={{ margin: '0 5px' }}
          height="20px"
        />
      ))
    );

    const handleLuCodeClick = (luCode: string) => {
      // Edit the medication to add LU code to comments
      const currentComments = medication.comments || '';
      const luText = `LU ${luCode}`;
      const newComments = currentComments ? `${currentComments}\n${luText}` : luText;

      // Update the medication in the medications array
      const updatedMedications = medications.map((med: any) =>
        med.id === medication.id ? { ...med, comments: newComments } : med,
      );
      setValue('medications', updatedMedications);
    };

    const odbInfo = medication.drugbank_product?.dpd_id ? (
      <OdbCoverageInfo
        din={medication.drugbank_product.dpd_id}
        placement="top"
        onLuCodeClick={handleLuCodeClick}
        /*@ts-ignore*/
        includePrice={false}
      />
    ) : null;

    return (
      <React.Fragment>
        {allergyIcons}
        {odbInfo}
      </React.Fragment>
    );
  }

  const allergiesWarning = useMemo(() => {
    let allergies = _.flatten(
      Object.values(matchingAllergies).filter((allergies: any) => allergies?.length > 0),
    ).map(({ allergy_name }: any) => allergy_name);
    if (allergies.length > 0) return `WARNING: Found allergies to ${allergies.join(', ')}`;
  }, [matchingAllergies]);

  return (
    <>
      {showMedicalOrderModal && (
        /*@ts-ignore*/
        <MedicalOrderModal
          isOpen={showMedicalOrderModal}
          onClose={() => setShowMedicalOrderModal(false)}
          patient={props.patient}
          editable
        />
      )}
      {props.onClose !== undefined && (
        <TopBar title="Prescription" onClose={props.onClose} showCloseButton={showCloseButton}>
          <div className="flex-grow" />
          {/*@ts-ignore*/}
          <Calculator
            type="pediatric-dose"
            name="Pediatric Dose Calculator"
            icon="baby"
            calculator={<PediatricDoseCalculator patient={props.patient} />}
            buttonStyle={{ width: '220px', margin: 0 }}
            patient={props.patient}
          />
          <DefaultButton
            name="Create Medical Order"
            icon="note"
            onClick={() => setShowMedicalOrderModal(true)}
            style={{ width: '200px', marginLeft: '15px' }}
          />
        </TopBar>
      )}
      <div className="details-panel new-prescription">
        <div>
          {props.openPreviewModal && (
            <DefaultModal
              key="preview-document-modal"
              className="large-modal"
              content={
                /*@ts-ignore*/
                <PreviewDocumentModal
                  url={props.pdfData?.url}
                  type={props.pdfData?.type}
                  onClose={() => props.setOpenPreviewModal(false)}
                  button={
                    <div style={{ width: '600px', margin: 'auto' }}>
                      <DefaultButton
                        name="Save and Print"
                        onClick={() => props.addPrescription(false, true)}
                        icon="print"
                        style={{
                          width: '190px',
                          marginTop: '5px',
                          display: 'inline-block',
                        }}
                      />
                      {!props.isIntern && (
                        <DefaultButton
                          name="Save and Fax"
                          icon="fax"
                          onClick={() => props.addPrescription(true)}
                          style={{
                            width: '190px',
                            marginTop: '5px',
                            marginLeft: '20px',
                            display: 'inline-block',
                          }}
                          disabled={!CPPUtils.getPharmacyFax(pharmacy)}
                        />
                      )}
                      <DefaultButton
                        name="Save"
                        icon="floppy-disk"
                        className="blue"
                        onClick={() => props.addPrescription(false, false)}
                        style={{
                          width: '100px',
                          marginTop: '5px',
                          marginLeft: '20px',
                          display: 'inline-block',
                        }}
                      />
                    </div>
                  }
                />
              }
              isOpen={props.openPreviewModal}
              onRequestClose={() => props.setOpenPreviewModal(false)}
            />
          )}
        </div>
        {!!props.note && (
          <div className="row form" style={{ marginBottom: '10px' }}>
            <span className="small-title">Refill Notes from Patient</span>
            <pre>{props.note}</pre>
          </div>
        )}
        <div className="prescription-input-container">
          <div className="prescription-input-main">
            <div className="row pretty-box new-prescription-form">
              <div className="npf-layout">
                <div className="npf-main">
                  <DrugbankInputs
                    change={setValue}
                    selectProduct={props.selectProduct}
                    hideFavorites={hideFavorites}
                    hideProduct
                    key={props.drugbankKey}
                  />
                  <DrugInputs {...props} change={setValue} control={control} />
                  <div>
                    <DefaultButton
                      name="Add Medication"
                      onClick={handleSubmit(addMedication)}
                      style={{
                        width: '145px',
                        marginRight: '20px',
                      }}
                      disabled={!isValid || !props.name}
                    />
                    <DefaultButton
                      name="Clear Medication"
                      className="blue"
                      onClick={() => clearMedication()}
                      style={{ width: '145px' }}
                    />
                  </div>
                </div>
                {drugbankProduct && !aiSuggestionsVisible && (
                  <aside className="npf-aside">
                    <Product product={drugbankProduct} change={setValue} />
                  </aside>
                )}
              </div>
            </div>
          </div>
          {aiSuggestionsVisible && (
            <div className="prescription-suggestions-sidebar">
              {showAISuggestions && !!appointment && (
                <PrescriptionSuggestions
                  appointment={appointment}
                  onAddMedication={addMedication}
                  onEditMedication={editMedication}
                />
              )}
              {drugbankProduct && <Product product={drugbankProduct} change={setValue} />}
            </div>
          )}
        </div>

        <div className="pretty-box medications-section">
          <div className="pretty-box-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon src="prescription-bottle-medical" width="16px" style={{ marginRight: '8px' }} />
              <span className="small-title">Medications Prescribed</span>
            </div>
            <IconButton onClick={clearMedications} size="small">
              <Icon
                src="trash"
                style={{ cursor: 'pointer' }}
                alt="Clear all medications"
                disabled={medications.length === 0}
                showTooltip={true}
              />
            </IconButton>
          </div>
          <StackItemsView
            key={medications.map(({ id }: any) => id).join('')}
            stack={medications}
            onDeleteItem={deleteMedication}
            describeItem={(medication: any) =>
              CPPUtils.getPrescriptionMedicationSummary(medication)
            }
            onEditItem={editMedication}
            additionalButton={(item: any) => <FavoriteDrugIcon item={item} favorites={favorites} />}
            /*@ts-ignore*/
            onMoveEnd={(list: any) => {
              setValue('medications', list);
            }}
            hideStatus={true}
            getAdditionalElement={(item: any) => getMedicationInteraction(item)}
          />
        </div>
        <div className="pharmacy-section pretty-box">
          <div className="pretty-box-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icon src="house-chimney-medical" width="16px" style={{ marginRight: '8px' }} />
              <span className="small-title">Pharmacy</span>
            </div>
          </div>
          <div className="row pharmacist">
            <PrescriptionTextArea
              control={control}
              id="additional_information"
              name="additional_information"
              label="Additional Information for Pharmacist"
            />
          </div>
          <PharmacySelector
            patient={props.patient}
            pharmacy={pharmacy}
            onSelect={onSelectPharmacy}
            onRemove={removePharmacy}
          />
        </div>
        <div className="row bottom-button-wrapper">
          <ConfirmationButton
            name="Cross-taper"
            className="white"
            loading={loadingCrossTaper}
            onClick={(e: any, form: any) => onCrossTaper(form)}
            icon="arrow-right-arrow-left"
            confirmation={{
              message: 'What medications are being crossed-tapered?',
              form: [
                { type: 'textField', label: 'From' },
                { type: 'textField', label: 'To' },
                { type: 'textField', label: '# Weeks' },
                { type: 'textField', label: 'Notes' },
                { type: 'switch', label: 'Start tapering in week 1' },
              ],
            }}
          />
          <BlankPrescriptionTemplate {...props} />
          <DefaultSwitch
            reversed={true}
            padded={false}
            name="hide"
            checked={props.hide}
            onChange={(_: any, checked: boolean) => {
              setValue('hide', checked);
            }}
            label="Hide Prescription"
          />
          <AddAttachmentButton
            loading={loadingAdd || loadingPreview}
            patient={props.patient}
            change={setValue}
            formAttachments={props.formAttachments}
          />
          <ConfirmationButton
            className="save-button white"
            name="Preview"
            onClick={previewPrescription}
            loading={loadingPreview}
            icon="file-pdf"
            style={{ width: '130px' }}
            disabled={loadingAdd || medications.length === 0}
            confirmation={{
              message: allergiesWarning,
              confirmLabel: allergiesWarning ? 'I Understand' : undefined,
            }}
          />
          <ConfirmationButton
            key={loadingAdd}
            className="save-button blue"
            name="Save"
            onClick={() => props.addPrescription(false, false)}
            confirmation={{
              additionalButton: (
                <>
                  <DefaultButton
                    name="Save & Print"
                    onClick={() => props.addPrescription(false, true)}
                    icon="print"
                    style={{ width: '140px' }}
                  />
                  <DefaultButton
                    name="Save & Fax"
                    onClick={() => props.addPrescription(true, false)}
                    icon="fax"
                    style={{ width: '130px' }}
                  />
                </>
              ),
              message: `${allergiesWarning || ''}Are you sure you want to save this prescription?`,
              confirmIcon: 'save',
              confirmLabel: 'Save',
            }}
            loading={loadingAdd}
            icon="save"
            style={{ width: '110px' }}
            disabled={loadingPreview || medications.length === 0}
          />
        </div>
      </div>
    </>
  );
};

const AddAttachmentButton = ({ loading, formAttachments = [], patient, change }: any) => {
  const [attachmentModal, setAttachmentModal] = useState(false);
  return (
    <>
      <DefaultButton
        name={`${formAttachments.length} attachments`}
        icon="paperclip"
        onClick={() => setAttachmentModal(true)}
        disabled={loading}
      />
      {attachmentModal && (
        <DefaultModal
          key="add-document-attachment-modal"
          className="large-modal add-document-attachment-modal"
          wrap={true}
          padding={false}
          title="Add Prescription Attachments"
          content={
            <AddDocumentAttachmentModal
              patient={patient}
              selected={formAttachments}
              onSubmit={(attachments: any) => {
                change('attachments', attachments);
                setAttachmentModal(false);
              }}
            />
          }
          isOpen={attachmentModal}
          onRequestClose={() => setAttachmentModal(false)}
        />
      )}
    </>
  );
};

const BlankPrescriptionTemplate = ({ blankTemplate, patient }: any) => {
  let [open, setOpen] = useState(false);
  return (
    <div className="blank-prescription-button">
      <DefaultButton
        name="Open Blank Prescription Template"
        style={{ width: '270px' }}
        className="blue"
        onClick={() => setOpen(true)}
        disabled={!blankTemplate}
      />
      {open && (
        <DefaultModal
          className="large-modal"
          key="document-template-modal"
          content={
            <DocumentTemplate
              title={(blankTemplate || {}).description}
              templateId={(blankTemplate || {}).id}
              onClose={() => setOpen(false)}
              patient={patient}
              onSaveCompleted={() => setOpen(false)}
            />
          }
          isOpen={open}
          onRequestClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

export const DrugInputs = (props: any) => {
  const { control, change } = props;

  let lastsUntil = CPPUtils.calculateDrugLastsUntil({
    time: props.time,
    time_unit: props.time_unit,
    dosing_instructions: props.dosing_instructions,
    refills: props.refills,
  });

  // Controlled substances (CDSA) must never be patient-refillable through the app
  // — a refill is a fresh prescribing decision the NP has to make at a visit.
  // When the selected drug is controlled, force refills OFF and lock the toggle.
  const isControlled = isControlledSchedule(props.drugbank_product?.schedule);
  useEffect(() => {
    if (isControlled && !props.disable_refills) {
      change('disable_refills', true);
    }
  }, [isControlled, props.disable_refills, change]);

  const [complexDosing, setComplexDosing] = useState(false);

  const showComplexDosing = complexDosing || props.dosing_instructions?.length > 1;

  const renderTextField = (name: string, label: string, width = 60) => (
    <PrescriptionTextField control={control} name={name} label={label} width={width} />
  );

  const renderAutoCompleteField = (name: string, label: string, width = 160) => (
    <PrescriptionAutoCompleteField control={control} name={name} label={label} width={width} />
  );

  const quantityFields = (
    <>
      {renderTextField('quantity', 'Quantity')}
      {renderAutoCompleteField('quantity_unit', 'Unit', 100)}
      {renderTextField('comments', 'Comments', 300)}
    </>
  );

  return (
    <div className="drug-input">
      <div className="row" style={{ paddingTop: 0 }}>
        {renderTextField('name', 'Medication Name', 325)}
        {showComplexDosing && renderAutoCompleteField('route', 'Route', 100)}
        {!showComplexDosing && (
          <DosingInstructionsInput
            /*@ts-ignore*/
            key={showComplexDosing}
            routeField={renderAutoCompleteField('route', 'Route', 100)}
            dosingInstructions={props.dosing_instructions || []}
            change={change}
            allowAdd={false}
          />
        )}
        {renderTextField('refills', 'Refills')}
        {!showComplexDosing && quantityFields}
        {lastsUntil && (
          <DefaultDatePicker
            key={lastsUntil}
            name="duration"
            label="Lasts until"
            value={lastsUntil}
            disabled={true}
            style={{ width: '120px' }}
          />
        )}
      </div>
      {showComplexDosing && (
        <>
          <div className="row" style={{ padding: '0 0' }}>
            <DosingInstructionsInput
              /*@ts-ignore*/
              key={showComplexDosing}
              dosingInstructions={props.dosing_instructions || []}
              change={change}
              allowAdd={true}
            />
          </div>
          <div className="row" style={{ paddingTop: 0 }}>
            {quantityFields}
          </div>
        </>
      )}
      <div className="row" style={{ paddingTop: 0 }}>
        <DefaultSwitch
          reversed={true}
          padded={false}
          name="prn"
          checked={props.prn}
          onChange={(_: any, checked: boolean) => {
            change('prn', checked);
          }}
          label="PRN"
        />
        <DefaultSwitch
          reversed={true}
          padded={false}
          disabled={props.dosing_instructions?.length > 1}
          label="Complex Dosing"
          name="complex_dosing"
          value={complexDosing}
          onChange={(_: any, checked: boolean) => {
            setComplexDosing(checked);
          }}
        />
        <div className="input-wrapper">
          {/* A disabled control doesn't fire hover events, so wrap it in a span
              for the Tooltip to anchor to. Empty title => no tooltip (non-controlled). */}
          <Tooltip
            title={
              isControlled
                ? 'Controlled substance (CDSA): patient-initiated refill requests are disabled — a refill requires an appointment with the prescriber.'
                : ''
            }
          >
            <span>
              <DefaultSwitch
                reversed={true}
                padded={false}
                disabled={isControlled}
                label="Allow Refill Requests"
                name="disable_refills"
                value={!isControlled && !props.disable_refills}
                checked={!isControlled && !props.disable_refills}
                defaultChecked={!props.disable_refills}
                onChange={(_: any, checked: boolean) => {
                  change('disable_refills', !checked);
                }}
              />
            </span>
          </Tooltip>
          <Info
            tooltip={
              isControlled
                ? 'This is a controlled substance (CDSA), so patient-initiated refill requests are disabled — a refill requires an appointment with the prescriber.'
                : 'Allows patient to request a refill request for this medication through the patient app'
            }
          />
        </div>
      </div>
    </div>
  );
};

export const DrugbankInputs = ({
  change,
  selectProduct,
  hideFavorites = false,
  hideProduct = false,
}: any) => {
  const dispatch = useAppDispatch();

  const favorites = useAppSelector((state) => state.drugs.favoriteMedications);

  const [searchTimeout, setSearchTimeout] = useState<any>(undefined);
  const [product, setProduct] = useState<any>();
  const [drugNames, setDrugNames] = useState<any>();
  const [drugStrengths, setDrugStrengths] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [drugRoutes, setDrugRoutes] = useState<any[]>([]);
  const [strength, setStrength] = useState<any>();

  const routeSelectRef = useRef<any>(null);
  const strengthSelectRef = useRef<any>(null);
  const [showTagManager, setShowTagManager] = useState(false);

  // Auto-focus route select when it appears
  useEffect(() => {
    if (drugRoutes.length > 1 && routeSelectRef.current) {
      setTimeout(() => {
        routeSelectRef.current?.focus();
      }, 100);
    }
  }, [drugRoutes]);

  // Auto-focus strength select when it appears
  useEffect(() => {
    if (drugStrengths.length > 1 && strengthSelectRef.current) {
      setTimeout(() => {
        strengthSelectRef.current?.focus();
      }, 100);
    }
  }, [drugStrengths]);

  function chooseFavorite(value: any) {
    value = CPPUtils.convertMedicationDosingInformation(value);
    clearMedication();
    selectProduct(null, null);
    if (value) {
      Object.keys(value).forEach((key) => {
        if (value[key]) {
          change(key, value[key]);
        }
      });
      if (value.quantity)
        setTimeout(() => {
          change('quantity', value.quantity);
        }, 1);
    }
  }

  function clearMedication() {
    setDrugNames(undefined);
    setDrugStrengths([]);
    setProducts([]);
    setDrugRoutes([]);
    setStrength(undefined);
  }

  function chooseProduct(product: any) {
    setProduct(product);
    if (product) {
      let name = product.prescribable_name;
      selectProduct(name, product);
    }
  }

  async function searchDrugWrapper(name: string) {
    let result = await dispatch(api.Drugs.searchDrug({ name }));
    if (result) {
      setDrugNames(result);
      setDrugRoutes([]);
      setDrugStrengths([]);
      setProducts([]);
      setProduct(undefined);
    }
  }

  function onSearchChange(text: string) {
    clearTimeout(searchTimeout);
    let timeout = setTimeout(async () => {
      if (text && text !== '' && text.length > 2) {
        await searchDrugWrapper(text);
      }
    }, UIUtils.SEARCH_TYPE_TIMEOUT);
    setSearchTimeout(timeout);
  }

  async function findRoutes(drug: any) {
    if (drug) {
      let id = drug.drugbank_pcid;
      setDrugRoutes([]);
      setStrength(undefined);
      setDrugStrengths([]);
      setProducts([]);
      let result = await dispatch(api.Drugs.getRoutes({ id }));
      if (result) {
        setDrugRoutes(result);
        if (result.length === 1) {
          await findStrengths(result[0]);
        }
      }
    }
  }

  async function findStrengths(drug: any) {
    if (drug) {
      let id = drug.drugbank_pcid;
      setStrength(undefined);
      setDrugStrengths([]);
      setProducts([]);
      let result = await dispatch(api.Drugs.getStrengths({ id }));
      if (result) {
        setDrugStrengths(result);
        if (result.length === 1) {
          await findProducts(result[0]);
        } else if (result.length === 0) {
          findProducts(drug);
        }
      }
    }
  }

  async function findProducts(drug: any) {
    if (drug) {
      let id = drug.drugbank_pcid;
      setProducts([]);
      setStrength(drug);
      let result = await dispatch(api.Drugs.getProducts({ id }));
      if (result) {
        setProducts(result);
        if (result.length > 0) {
          //Generics first
          let chosen = result.filter((p: any) => p.generic)[0];
          if (!chosen) {
            //if no generics, then capsules first
            chosen = result.filter((p: any) => p.dosage_form === 'Capsule')[0];
          }
          chooseProduct(chosen || result[0]);
        }
      }
    }
  }

  return (
    <div className="drug-inputs">
      <div className="drop-downs">
        {!hideFavorites && (
          <div className="favorites-row" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <div className="react-select favorites" style={{ flex: 1 }}>
              <DefaultReactSelect
                /*@ts-ignore*/
                loading={false}
                placeholder="Search Drugs (Favorites)..."
                onChange={(item: any) => chooseFavorite(item.value)}
                options={favoritesToOptions(favorites)}
              />
            </div>
            <Icon
              src="star"
              fontAwesomeStyle="fas"
              alt="Manage Favorite Drugs"
              showTooltip={true}
              width="24px"
              className="blue"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowTagManager(true)}
            />
          </div>
        )}
        <div className="react-select">
          <DefaultReactSelect
            /*@ts-ignore*/
            filterOption={() => true}
            loading={false}
            placeholder="Search Drugs (DrugBank)..."
            onChange={(item: any) => findRoutes(item.value)}
            onInputChange={onSearchChange}
            options={dataToOptions(drugNames)}
          />
        </div>
        {drugRoutes.length > 1 && (
          <DefaultReactSelect
            ref={routeSelectRef}
            /*@ts-ignore*/
            filterOption={() => true}
            defaultMenuIsOpen={drugRoutes.length > 1}
            autoFocus={true}
            openMenuOnFocus={true}
            preventScrollOnFocus={true}
            loading={false}
            isSearchable={false}
            placeholder={`Select Route (${drugRoutes.length})`}
            onChange={(item: any) => findStrengths(item.value)}
            options={dataToOptions(drugRoutes)}
          />
        )}
        {drugStrengths.length > 1 && (
          <div className="flex">
            <DefaultReactSelect
              ref={strengthSelectRef}
              /*@ts-ignore*/
              filterOption={() => true}
              defaultMenuIsOpen={drugStrengths.length > 1}
              autoFocus={true}
              openMenuOnFocus={true}
              preventScrollOnFocus={true}
              loading={false}
              isSearchable={false}
              placeholder={`Select Strength (${drugStrengths.length})`}
              onChange={(item: any) => findProducts(item.value)}
              options={dataToOptions(drugStrengths)}
            />
            {!!product && (
              <div className="copy" style={{ alignSelf: 'center' }}>
                <Icon
                  src="copy"
                  alt="Copy Name"
                  showTooltip={true}
                  width="20px"
                  onClick={() => change('name', (strength || {}).name)}
                />
              </div>
            )}
          </div>
        )}
        {products.length > 1 && (
          <DefaultReactSelect
            /*@ts-ignore*/
            filterOption={() => true}
            defaultMenuIsOpen={false}
            preventScrollOnFocus={true}
            loading={false}
            isSearchable={false}
            placeholder={`Select Product (${products.length})`}
            onChange={(item: any) => chooseProduct(item.value)}
            options={dataToOptions(products, ['prescribable_name'], true)}
          />
        )}
      </div>
      {!hideProduct && (
        <div className="product">
          {product !== undefined && <Product product={product} change={change} />}
        </div>
      )}
      {showTagManager && (
        <FavoritesTagManager
          isOpen={showTagManager}
          onRequestClose={() => setShowTagManager(false)}
        />
      )}
    </div>
  );
};

const Product = ({ product, change }: any) => {
  const handleLuCodeClick = (luCode: string) => {
    change('comments', (currentComments: string) => {
      const luText = `LU${luCode}`;
      return currentComments ? `${currentComments} ${luText}` : luText;
    });
  };

  const formatStrength = (strength: any) => {
    if ((strength || {}).number) return `${strength.number} ${strength.unit}`;
    if ((strength || {}).amount) return `${strength.amount} per ${strength.per} ${strength.units}`;
    return '';
  };

  const labellerName = (product.labeller || {}).name;
  const ingredients = product.ingredients || [];

  return (
    <div className="drug-details pretty-box">
      <div className="pretty-box-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon src="capsules" width="16px" style={{ marginRight: '8px' }} />
          <span className="small-title">Drug Information</span>
        </div>
      </div>
      <div className="drug-details-header">
        <span className="drug-details-name">{product.name}</span>
        {product.prescribable_name && (
          <span className="drug-details-subtitle">{product.prescribable_name}</span>
        )}
      </div>

      <div className="drug-details-tags">
        <span className={`dd-chip ${product.approved ? 'dd-chip--success' : 'dd-chip--danger'}`}>
          {product.approved ? 'Approved' : 'Not approved'}
        </span>
        <span className={`dd-chip ${product.generic ? 'dd-chip--info' : 'dd-chip--neutral'}`}>
          {product.generic ? 'Generic' : 'Brand'}
        </span>
        {product.mixture && <span className="dd-chip dd-chip--neutral">Mixture</span>}

        {product.dosage_form && <DrugDetailPair label="Form" value={product.dosage_form} />}
        {product.route && <DrugDetailPair label="Route" value={product.route} />}
        {product.schedule && <DrugDetailPair label="Schedule" value={product.schedule} />}
        {labellerName && <DrugDetailPair label="Labeller" value={labellerName} />}
      </div>

      {ingredients.length > 0 && (
        <div className="drug-details-section drug-details-section--ingredients">
          <span className="dd-section-title">
            {ingredients.length > 1 ? 'Ingredients' : 'Ingredient'}
          </span>
          <div className="drug-details-tags">
            {ingredients.map((ingredient: any, index: number) => (
              <DrugDetailPair
                key={`${ingredient.name}-${index}`}
                label={ingredient.name}
                value={formatStrength(ingredient.strength) || '—'}
                variant="ingredient"
              />
            ))}
          </div>
        </div>
      )}

      {product.dpd_id && (
        <div className="drug-details-section drug-details-coverage">
          <OdbCoverageInfo din={product.dpd_id} onLuCodeClick={handleLuCodeClick} />
        </div>
      )}
    </div>
  );
};

const DrugDetailPair = ({
  label,
  value,
  variant,
}: {
  label: string;
  value: React.ReactNode;
  variant?: string;
}) => (
  <span className={`dd-pair${variant ? ` dd-pair--${variant}` : ''}`}>
    <span className="dd-pair-key">{label}</span>
    <span className="dd-pair-val">{value}</span>
  </span>
);

function dataToOptions(data: any[] = [], nameFields = ['name'], includeLabeller = false) {
  return data.map((value) => {
    let label = nameFields.map((field) => value[field]).join(' - ');

    if (includeLabeller) label += ` - ${value.labeller.name}`;

    return { value, label };
  });
}

function favoritesToOptions(data: any[] = []) {
  return data.map((item: any) => {
    let value = { ...item.value };
    delete value.id;
    let label = CPPUtils.getPrescriptionMedicationSummary(value);

    // Add tags to label for search functionality
    if (item.tags && item.tags.length > 0) {
      label += ` [${item.tags.join(', ')}]`;
    }

    return { value, label };
  });
}

// Inline RHF-based field components replacing redux-form Field wrappers

const PrescriptionTextArea = ({
  control,
  name,
  id,
  label,
}: {
  control: Control<any>;
  name: string;
  id?: string;
  label: string;
}) => (
  <>
    <span className="small-title" style={{ marginTop: '8px' }}>
      {label}
    </span>
    <TextareaAutosize id={id ?? `${name}-field`} name={name} control={control} minRows={1} />
  </>
);

const PrescriptionAutoCompleteField = ({
  control,
  id,
  name,
  label,
  width = 160,
}: {
  id?: string;
  control: Control<any>;
  name: string;
  label: string;
  width?: number;
}) => (
  <DefaultAutoComplete
    name={name}
    id={id ?? `${name}-field`}
    control={control}
    placeholder={label}
    style={{ width: width + 'px', marginLeft: 0, marginRight: 0 }}
    dataSource={DRUG_AUTOCOMPLETE_SOURCE[name as keyof typeof DRUG_AUTOCOMPLETE_SOURCE]}
  />
);

const PrescriptionTextField = ({
  control,
  name,
  id,
  label,
  width = 60,
}: {
  control: Control<any>;
  name: string;
  label: string;
  id?: string;
  width?: number;
}) => (
  <DefaultTextField
    name={name}
    id={id ?? `${name}-field`}
    control={control}
    label={label}
    className={label}
    style={{ width: width + 'px' }}
  />
);

const DEFAULT_ARRAY: any[] = [];

export const DefaultPharmacy = ({ patient, setPharmacy }: any) => {
  const dispatch = useAppDispatch();

  const prescriptions =
    useAppSelector((state) => state.healthRecords.byId[patient.id]?.prescriptions) || DEFAULT_ARRAY;

  const [editPharmacy, setEditPharmacy] = useState(false);

  const defaultPharmacy = useDefaultPharmacy(patient);

  // Only offer recently-used pharmacies that still resolve in the current
  // directory; a stale RX-snapshot id won't bind to the patient's default.
  const { pharmacies: previousPharmacies } = useValidPreviousPharmacies(
    patient ? prescriptions : DEFAULT_ARRAY,
    defaultPharmacy,
  );

  async function onSavePharmacy(pharmacy: any) {
    if (pharmacy) {
      if (setPharmacy) setPharmacy(pharmacy);
      let updatedPatient = { ...patient, default_pharmacy_id: pharmacy.id };
      await dispatch(
        api.Patients.updatePatient({
          id: patient.id,
          patient: updatedPatient,
        }),
        { errorTitle: 'Update Preferred Pharmacy' },
      );
    }
  }

  return (
    <>
      {editPharmacy && (
        <DefaultModal
          key="pharmacy-search-modal"
          content={
            /*@ts-ignore*/
            <PharmacySearchModal
              onSelect={(pharmacy: any) => {
                setEditPharmacy(false);
                onSavePharmacy(pharmacy);
              }}
              initialPharmacies={previousPharmacies}
              defaultPharmacyId={defaultPharmacy?.id}
              backButton={() => setEditPharmacy(false)}
            />
          }
          isOpen={editPharmacy}
          onRequestClose={() => setEditPharmacy(false)}
        />
      )}
      <DefaultButton
        name="Change Preferred Pharmacy"
        onClick={() => setEditPharmacy(true)}
        className="default-pharmacy-button white"
        style={{
          width: '100%',
        }}
      />
    </>
  );
};

const FavoriteDrugIcon = ({ item }: any) => {
  const dispatch = useAppDispatch();

  const favorites = useAppSelector((state) => state.drugs.favoriteMedications);

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const matchingFavorite = useMemo(() => {
    return favorites.find(
      (favorite: any) =>
        CPPUtils.getPrescriptionMedicationSummary(item) ===
        CPPUtils.getPrescriptionMedicationSummary(favorite.value),
    );
  }, [item, favorites]);

  async function handleStarClick() {
    if (matchingFavorite) {
      // Remove favorite
      setLoading(true);
      await dispatch(api.Drugs.removeFavoriteMedication({ id: matchingFavorite.id! }));
      setLoading(false);
    } else {
      // Open modal for adding with tags
      setShowModal(true);
    }
  }

  return (
    <>
      <Icon
        src="star"
        fontAwesomeStyle={!!matchingFavorite ? 'fas' : 'far'}
        width="20px"
        className={!matchingFavorite ? 'favorite-icon' : 'favorite-icon blue'}
        alt={!matchingFavorite ? 'Add to Favorites' : 'Remove from Favorites'}
        showTooltip={true}
        loading={loading}
        onClick={handleStarClick}
      />
      {showModal && (
        <AddToFavoritesModal
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
          medication={item}
        />
      )}
    </>
  );
};

export const DRUG_AUTOCOMPLETE_SOURCE = {
  dosage_unit: [
    'mg',
    'g',
    'tab',
    'tabs',
    'mL',
    'gtt',
    'gtts',
    'spray',
    'sprays',
    'application',
    'capsule(s)',
    'puff(s)',
  ],
  quantity_unit: ['tabs', 'mg', 'g', 'mL', 'capsule(s)', 'application'],
  route: [
    'PO',
    'PR',
    'IM',
    'IV',
    'TP',
    'SL',
    'BUCC',
    'INH',
    'right eye',
    'left eye',
    'both eyes',
    'left ear',
    'right ear',
    'both ears',
    'each nostril',
    'PV',
    'SQ',
  ],
  time_unit: ['days', 'hours', 'minutes', 'weeks', 'months'],
  frequency: [
    'daily',
    'every other day',
    'Once',
    'STAT',
    'BID',
    'TID',
    'QID',
    'QHS',
    'Q4h',
    'Q4-6h',
    'QWK',
    'QAM',
    'QPM',
  ],
};

const _DrugInputView = ({ medication, drugbankKey, onFormChange, hideFavorites }: any) => {
  const { control, setValue, watch } = useForm({
    defaultValues: medication || {},
  });

  const formValues = watch();

  useEffect(() => {
    if (medication) {
      for (let key in medication) {
        setValue(key, medication[key]);
      }
    }
  }, [medication, setValue]);

  useEffect(() => {
    onFormChange?.(formValues);
  }, [formValues]);

  const selectProduct = useCallback(
    (name: string | null, product: any) => {
      setValue('name', name);
      setValue('drugbank_product', product);
    },
    [setValue],
  );

  return (
    <div className="drug-input">
      <DrugbankInputs
        change={setValue}
        selectProduct={selectProduct}
        hideFavorites={hideFavorites}
        key={drugbankKey}
      />
      <DrugInputs {...medication} change={setValue} control={control} />
    </div>
  );
};

export const DrugInputView = _DrugInputView;

const PrescriptionMedicationCard = ({ medication, onEdit, onAdd }: any) => {
  return (
    <div className="suggestion-card">
      <div style={{ display: 'flex', gap: '8px', marginRight: '12px' }}>
        <Icon
          src="pen"
          width="16px"
          style={{ cursor: 'pointer' }}
          className="suggestion-icon-button"
          onClick={onEdit}
          alt="Edit medication"
          showTooltip={true}
        />
        <Icon
          src="plus"
          width="16px"
          style={{ cursor: 'pointer' }}
          className="suggestion-icon-button blue"
          onClick={onAdd}
          alt="Add medication"
          showTooltip={true}
        />
      </div>
      <div className="suggestion-content">
        <div className="medication-name">
          {CPPUtils.getPrescriptionMedicationSummary(medication)}
        </div>
      </div>
    </div>
  );
};

function PrescriptionSuggestions({ appointment, onAddMedication, onEditMedication }: any) {
  const dispatch = useAppDispatch();
  const { unreactiveNoteValue } = useCPPContext();
  const latestDraft = useAppSelector(
    (state) =>
      _(state.healthRecords.ownDrafts)
        .filter((it: any) => it.patient_id === appointment.id)
        .orderBy(['date'], ['desc'])
        .first()?.note?.value,
  );

  const { loading, medications, generatePrescription, invalidCache } = useGeneratePrescription(
    appointment,
    appointment.patient_id,
    //@ts-ignore
    unreactiveNoteValue.current ?? latestDraft,
  );

  useEffect(() => {
    if (invalidCache) {
      generatePrescription();
    }
  }, [invalidCache, generatePrescription]);

  return (
    <div className="prescription-suggestions pretty-box" data-has-items={medications.length > 0}>
      <div className="suggestions-header pretty-box-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon src="sparkles" width="16px" style={{ marginRight: '8px' }} />
          <span className="small-title">AI Medication Suggestions</span>
        </div>
        <IconButton onClick={() => generatePrescription()} size="small">
          <Icon
            src="rotate"
            style={{ cursor: 'pointer' }}
            loading={loading}
            alt="Refresh suggestions"
            showTooltip={true}
          />
        </IconButton>
      </div>
      <div className="suggestions-list">
        {medications.map((medication: any, index: number) => (
          <PrescriptionMedicationCard
            key={index}
            medication={medication}
            onEdit={() => onEditMedication(medication)}
            onAdd={() => {
              onAddMedication(medication);
              // Usefulness metric: clinician accepted an AI medication suggestion.
              dispatch(
                api.Resource.recordAiSuggestionAccepted({
                  aiSuggestionAccepted: {
                    prompt: 'Generate Prescription',
                    patient_id: appointment.patient_id,
                    metadata: { medication: medication?.name },
                  },
                }),
              );
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default NewPrescriptionView;
